import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil, tap } from 'rxjs';
import { ScheduleAppointmentHelperService } from '../../../appointments/services/schedule-appointment-helper/schedule-appointment-helper.service';
import { getSeverityByStatusCode } from '../../../core/functions/app-insights-functions';
import { AppInsightsFeatureType } from '../../../core/models/app-insights-feature-type';
import { AppInsightsService } from '../../../core/services/app-insights/app-insights.service';
import { NavigationHistoryService } from '../../../core/services/navigation-history/navigation-history.service';
import { selectIsMobileBrowser } from '../../../core/store/selectors/app.selector';
import { clearAllState } from '../../../core/store/actions/app.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { NotificationPreferencesService } from '../../services/notification-preferences/notification-preferences.service';
import { NotificationTypesService } from '../../services/notification-types/notification-types.service';
import {
  getNotificationPreferences,
  getNotificationPreferencesFailure,
  getNotificationPreferencesSuccess,
  getNotificationTypes,
  getNotificationTypesFailure,
  getNotificationTypesSuccess,
  saveNotificationPreferences,
  saveNotificationPreferencesFailure,
  saveNotificationPreferencesSuccess,
} from '../actions/notification-preference.actions';

@Injectable()
export class NotificationPreferencesEffects {
  private readonly refillFlowRouteSegments = [
    '/medication-refills/',
    '/patient-pharmacies/',
    '/pharmacy-search/',
    '/pharmacy-search-results/',
    '/medication/verification',
    '/medication/confirmation/',
  ];

  private actions$ = inject(Actions);
  private notificationPreferencesService = inject(NotificationPreferencesService);
  private notificationTypesService = inject(NotificationTypesService);
  private scheduleAppointmentHelperService = inject(ScheduleAppointmentHelperService);
  private appInsightsService = inject(AppInsightsService);
  private navigationHistoryService = inject(NavigationHistoryService);
  private store = inject(Store);
  private router = inject(Router);
  private snackbar = inject(MatSnackBar);
  // Cancels on logout and account switch only. Patient switch is deliberately absent: consent is
  // user-scoped now, so the in-flight response is still valid for the same user after a switch.
  // The clearAllState arm is load-bearing rather than tidy — this slice lives at app level and
  // clear-state preserves only ['router', 'app'], so a response resolving after logout would
  // otherwise repopulate it with the previous account's EmailAddress and MobilePhoneNumber.
  private readonly cancelRequest$ = this.actions$.pipe(ofType(clearAllState));

  getNotificationPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getNotificationPreferences),
      mergeMap(() => {
        return this.notificationPreferencesService.getNotificationPreferences().pipe(
          takeUntil(this.cancelRequest$),
          map(preferences => {
            return getNotificationPreferencesSuccess({ preferences });
          }),
          catchError(err => {
            return of(getNotificationPreferencesFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  getNotificationTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getNotificationTypes, getNotificationPreferences),
      mergeMap(() => {
        return this.notificationTypesService.getNotificationTypes().pipe(
          takeUntil(this.cancelRequest$),
          map(notificationTypes => {
            return getNotificationTypesSuccess({ notificationTypes });
          }),
          catchError(err => {
            return of(getNotificationTypesFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  saveNotificationPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveNotificationPreferences),
      mergeMap(action => {
        return this.notificationPreferencesService
          .putNotificationPreferences(action.notificationPreferenceRequest)
          .pipe(
            // same protection the GET has: a save resolving after logout must not repopulate the slice
            takeUntil(this.cancelRequest$),
            map(preferences => {
              this.appInsightsService.trackEvent('Notifications.Save.Success', {
                feature: AppInsightsFeatureType.NOTIFICATIONS,
              });
              return saveNotificationPreferencesSuccess({ preferences, successRoute: action.successRoute });
            }),
            catchError(err => {
              this.appInsightsService.trackTrace('Notifications.Save.Failure', getSeverityByStatusCode(err.status), {
                feature: AppInsightsFeatureType.NOTIFICATIONS,
              });
              return of(saveNotificationPreferencesFailure({ error: err.error?.errors }));
            })
          );
      })
    );
  });

  navigateOnSendMessageSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(saveNotificationPreferencesSuccess),
        concatLatestFrom(() => [this.store.select(selectPatientId), this.store.select(selectIsMobileBrowser)]),
        tap(([action, patientId, isMobileBrowser]) => {
          this.snackbar.open('Notification preferences saved successfully!', undefined, {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });

          if (!isMobileBrowser && action.successRoute === 'appointments') {
            this.scheduleAppointmentHelperService.closeDialog();
          } else if (action.successRoute === 'medications') {
            const medicationsUrl = `/patient/${patientId}/medications`;
            const stepsBackToMedications = this.navigationHistoryService.stepsBackToUrl(medicationsUrl);
            if (stepsBackToMedications !== null) {
              window.history.go(-stepsBackToMedications);
              return;
            }

            const stepsBack = this.navigationHistoryService.stepsBackToPreviousUrl(
              url => !this.refillFlowRouteSegments.some(segment => url.includes(segment))
            );

            if (stepsBack !== null) {
              window.history.go(-stepsBack);
              return;
            }

            this.router.navigate(['patient', patientId, 'medications'], { replaceUrl: true });
          } else {
            const baseRoute = ['patient', patientId];
            const route = action.successRoute ? [...baseRoute, action.successRoute] : baseRoute;
            this.router.navigate(route, { replaceUrl: true });
          }
        })
      );
    },
    { dispatch: false }
  );
}
